import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["secondaryNavItems", "isBookmarksMenu", "menuTitle"];
import { GLOBAL_NAVIGATION_SIDEBAR_DEFAULT_COLLAPSED_WIDTH, GLOBAL_NAVIGATION_TOOLBAR_HEIGHT } from 'nav-meta/constants';
import { forwardRef } from 'react';
import styled, { css } from 'foundations-theming-specialty/utils';
import useNavItems from '../../hooks/useNavItems';
import useVerticalNavKeyboardControls from './hooks/useVerticalNavKeyboardControls';
import { EXPANDABLE_NAV_GROUP, VERTICAL_NAV_GROUP } from './constants';
import VerticalNavSecondaryMenuItem from './VerticalNavSecondaryMenuItem';
import { useLinkBadgeData } from './hooks/useLinkBadgeData';
import { MenuItemLevel } from '../../constants';
import verticalNavScrollbarStyles from './utils/verticalNavScrollbarStyles';
import VerticalNavExpandableList from './VerticalNavExpandableList';
import { NAV_ITEM_BOOKMARKS_GROUP_ID } from '../../constants/Bookmarks';
import useIsDevPortal from '../../hooks/useIsDevPortal';
import EmptyShortcutsMenuContent from './VerticalNavSecondaryMenuEmptyShortcutsContent';
import VerticalNavTertiaryMenu from './VerticalNavTertiaryMenu';
import useIsVerticalNavPinned from '../../hooks/useIsVerticalNavPinned';
import withTransientStyleProps from '../../utils/withTransientStyleProps';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledHeading = styled.h5.withConfig({
  displayName: "VerticalNavSecondaryMenu__StyledHeading",
  componentId: "sc-165k8ct-0"
})(({
  theme
}) => {
  return css(["font-size:16px;font-weight:600;line-height:18px;padding:8px 16px 12px;color:", ";margin:0;"], theme.color['global-shell-text-nav-item-hover']);
});
const StyledContainer = styled.div.withConfig({
  displayName: "VerticalNavSecondaryMenu__StyledContainer",
  componentId: "sc-165k8ct-1"
})(["left:calc( var( --global-nav-vertical-nav-width,", " ) + 4px );top:0;padding:0px 12px 12px 0px;position:fixed;"], GLOBAL_NAVIGATION_SIDEBAR_DEFAULT_COLLAPSED_WIDTH);
const Div = styled.div.withConfig({
  displayName: "VerticalNavSecondaryMenu__Div",
  componentId: "sc-165k8ct-2"
})([""]);
const StyledDiv = styled(withTransientStyleProps(Div))(({
  $isEmptyBookmarksMenu,
  theme
}) => {
  return css(["background:", ";border-radius:8px;list-style-type:none;margin:0;max-height:calc(100vh - ", " - 8px);min-width:212px;max-width:450px;box-shadow:", ";padding:12px 8px;clip-path:inset(0 -3px 0 0);width:", ";", " ul{list-style-type:none;margin:0;padding:0;white-space:nowrap;}"], theme.color['global-shell-fill-surface-panel-default'], GLOBAL_NAVIGATION_TOOLBAR_HEIGHT, theme.boxShadow['400'], $isEmptyBookmarksMenu ? '240px' : 'auto', verticalNavScrollbarStyles(theme));
});
const StyledHr = styled.hr.withConfig({
  displayName: "VerticalNavSecondaryMenu__StyledHr",
  componentId: "sc-165k8ct-3"
})(({
  theme
}) => {
  return css(["margin:12px auto;padding:0;background:", ";border:0;height:1px;display:block;width:calc(100% - 20px);"], theme.color['global-shell-border-subtle']);
});
export const VerticalNavSecondaryMenuContent = ({
  secondaryNavItem,
  isBookmarksMenu
}) => {
  const navItems = useNavItems();
  const {
    byId,
    activeItem
  } = navItems;
  const {
    onKeyDown
  } = useVerticalNavKeyboardControls({
    isBookmarksMenu
  });
  const isDevPortal = useIsDevPortal();
  const {
    path,
    label,
    primaryCategory,
    isBookmarked,
    children,
    nonContextualLabel
  } = byId[secondaryNavItem.id];
  const shouldHighlight = activeItem === secondaryNavItem.id;
  const linkBadgeData = useLinkBadgeData({
    id: secondaryNavItem.id,
    navItems
  });
  return /*#__PURE__*/_jsx(VerticalNavSecondaryMenuItem, {
    id: secondaryNavItem.id,
    path: path,
    label: isBookmarksMenu && nonContextualLabel ? nonContextualLabel : label,
    onKeyDown: onKeyDown({
      id: secondaryNavItem.id,
      hasParentMenu: true,
      shouldShiftFocusOnOpen: true
    }),
    shouldHighlight: shouldHighlight,
    linkBadgeData: linkBadgeData,
    menuItemLevel: MenuItemLevel.SECONDARY,
    primaryCategory: isBookmarksMenu ? NAV_ITEM_BOOKMARKS_GROUP_ID : primaryCategory,
    isBookmarked: !!isBookmarked,
    canBeBookmarked: !isDevPortal,
    isBookmarksMenu: isBookmarksMenu,
    children: children && children.length && !isBookmarksMenu ? /*#__PURE__*/_jsx(VerticalNavTertiaryMenu, {
      tertiaryNavItems: children
    }) : null
  }, secondaryNavItem.id);
};
const VerticalNavSecondaryMenuGroup = ({
  secondaryNavItem,
  withDivider = false
}) => {
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [withDivider ? /*#__PURE__*/_jsx(StyledHr, {
      "aria-hidden": "true"
    }) : null, secondaryNavItem.children.map(secondaryNavGroupItem => {
      if (secondaryNavGroupItem.id.startsWith(VERTICAL_NAV_GROUP)) {
        return /*#__PURE__*/_jsx(VerticalNavSecondaryMenuGroup, {
          secondaryNavItem: secondaryNavGroupItem,
          withDivider: true
        }, secondaryNavGroupItem.id);
      }
      return /*#__PURE__*/_jsx(VerticalNavSecondaryMenuContent, {
        secondaryNavItem: secondaryNavGroupItem,
        isBookmarksMenu: false
      }, secondaryNavGroupItem.id);
    })]
  });
};
const VerticalNavSecondaryMenu = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
      secondaryNavItems,
      isBookmarksMenu,
      menuTitle
    } = _ref,
    a11yProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const isEmptyBookmarksMenu = isBookmarksMenu && secondaryNavItems.length <= 0;
  const isVerticalNavPinned = useIsVerticalNavPinned();
  return /*#__PURE__*/_jsx(StyledContainer, {
    ref: ref,
    children: /*#__PURE__*/_jsx(StyledDiv, {
      "data-onboarding-hook": "secondary-menu",
      $isVerticalNavPinned: isVerticalNavPinned,
      $isEmptyBookmarksMenu: isEmptyBookmarksMenu,
      children: isEmptyBookmarksMenu ? /*#__PURE__*/_jsx(EmptyShortcutsMenuContent, {}) : /*#__PURE__*/_jsx(_Fragment, {
        children: /*#__PURE__*/_jsxs("ul", Object.assign({
          role: "menu"
        }, a11yProps, {
          children: [/*#__PURE__*/_jsx(StyledHeading, {
            className: "secondary-menu-heading",
            children: menuTitle
          }), secondaryNavItems.map((secondaryNavItem, index) => {
            if (secondaryNavItem.id.startsWith(VERTICAL_NAV_GROUP)) {
              return /*#__PURE__*/_jsx(VerticalNavSecondaryMenuGroup, {
                secondaryNavItem: secondaryNavItem,
                withDivider: index !== 0
              }, secondaryNavItem.id);
            }
            if (secondaryNavItem.id.startsWith(EXPANDABLE_NAV_GROUP)) {
              return /*#__PURE__*/_jsx(VerticalNavExpandableList, {
                id: secondaryNavItem.id,
                "data-test-id": secondaryNavItem.id,
                listItems: secondaryNavItem.children.map(secondaryNavGroupItem => {
                  return /*#__PURE__*/_jsx(VerticalNavSecondaryMenuContent, {
                    secondaryNavItem: secondaryNavGroupItem,
                    isBookmarksMenu: false
                  }, secondaryNavGroupItem.id);
                })
              }, secondaryNavItem.id);
            }
            return /*#__PURE__*/_jsx(VerticalNavSecondaryMenuContent, {
              secondaryNavItem: secondaryNavItem,
              isBookmarksMenu: !!isBookmarksMenu
            }, secondaryNavItem.id);
          })]
        }))
      })
    })
  });
});
export default VerticalNavSecondaryMenu;